import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { queryClient } from '../api/client'
import { getApiUrl } from '../api/config'
import { fetchClient } from '../api/typedClient'
import { TokenResponse, User } from '../types/auth'
import { cacheUtils } from '../utils/indexedDB'
import {
  clearAppStateStorage,
  clearFullSessionStorage,
} from '../utils/sessionCleanup'
import { createUserFromJWT, decodeToken } from './AuthContext.utils'

interface AuthContextType {
  isAuthenticated: boolean
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  login: (username: string, password: string) => Promise<void>
  logout: () => void
  resetPassword: (
    email: string,
    code: string,
    newPassword: string,
  ) => Promise<boolean>
  ssoAuthenticate: (token: string) => Promise<boolean>
  socialLogin: (provider: string, data: unknown) => Promise<void>
  isLoading: boolean
  error: string | null
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState<User | null>(null)
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [refreshToken, setRefreshToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Load tokens from localStorage on mount
  useEffect(() => {
    const checkAuth = async () => {
      const storedAccessToken = localStorage.getItem('accessToken')
      const storedRefreshToken = localStorage.getItem('refreshToken')

      if (storedAccessToken && storedRefreshToken) {
        try {
          const decoded = decodeToken(storedAccessToken)
          const expiresAt = decoded.exp * 1000
          if (expiresAt > Date.now()) {
            setAccessToken(storedAccessToken)
            setRefreshToken(storedRefreshToken)

            const userProfile = createUserFromJWT(storedAccessToken)
            setUser(userProfile)

            setIsAuthenticated(true)
          } else {
            await refreshAccessToken(storedRefreshToken)
          }
        } catch (err) {
          console.error('Failed to decode token during checkAuth', err)
          logout()
        }
      }
      setIsLoading(false)
    }

    checkAuth()
  }, [])

  const handleTokenResponse = useCallback(
    async (tokenResponse: TokenResponse) => {
      clearAppStateStorage()
      queryClient.clear()
      cacheUtils
        .clearAllCache()
        .catch((e) => console.error('Unhandled cache clear error:', e))
      const accessToken =
        tokenResponse.access_token || tokenResponse.accessToken
      const refreshToken =
        tokenResponse.refresh_token || tokenResponse.refreshToken

      localStorage.setItem('accessToken', accessToken)
      localStorage.setItem('refreshToken', refreshToken)

      const userProfile = createUserFromJWT(accessToken)
      setUser(userProfile)
      setIsAuthenticated(true)
      setAccessToken(accessToken)
      setRefreshToken(refreshToken)
    },
    [],
  )

  const refreshAccessToken = async (token: string) => {
    try {
      // @ts-expect-error TODO: Add /auth/refresh to OpenAPI spec
      const { data, error } = await fetchClient.POST('/auth/refresh', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!error && data) {
        const tokenResponse = data as unknown as TokenResponse
        await handleTokenResponse(tokenResponse)
      } else {
        logout()
      }
    } catch (err) {
      console.error('Token refresh failed:', err)
      logout()
    }
  }

  const login = async (username: string, password: string) => {
    setIsLoading(true)
    setError(null)

    try {
      const formData = new URLSearchParams()
      formData.append('grant_type', 'password')
      formData.append('username', username)
      formData.append('password', password)

      // TODO: /auth/token endpoint uses form-urlencoded content type
      // Keep as fetch() until OpenAPI spec is updated with proper content type
      const response = await fetch(getApiUrl('/auth/token'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData,
      })

      if (response.ok) {
        const tokenResponse: TokenResponse = await response.json()
        await handleTokenResponse(tokenResponse)
      } else {
        const errorData = await response.json()
        setError(errorData.detail || 'Login failed')
        throw new Error(errorData.detail || 'Login failed')
      }
    } catch (err) {
      console.error('Login error:', err)
      setError(err instanceof Error ? err.message : 'Login failed')
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const logout = useCallback(() => {
    clearFullSessionStorage()
    queryClient.clear()
    cacheUtils
      .clearAllCache()
      .catch((e) => console.error('Unhandled cache clear error:', e))
    setAccessToken(null)
    setRefreshToken(null)
    setUser(null)
    setIsAuthenticated(false)
    setError(null)
  }, [])

  const resetPassword = async (
    email: string,
    code: string,
    newPassword: string,
  ): Promise<boolean> => {
    try {
      const formData = new URLSearchParams()
      formData.append('email', email)
      formData.append('password_reset_code', code)
      formData.append('new_password', newPassword)

      const response = await fetch(getApiUrl('/meta/user/reset_password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData,
      })

      return response.ok
    } catch (err) {
      console.error('Reset password error:', err)
      return false
    }
  }

  const ssoAuthenticate = async (token: string): Promise<boolean> => {
    setIsLoading(true)
    setError(null)

    try {
      const { data, error } = await fetchClient.POST(
        // @ts-expect-error TODO: /auth/sso endpoint exists but takes different body format
        '/auth/sso',
        { body: { token } as never },
      )

      if (!error && data) {
        const tokenResponse = data as unknown as TokenResponse
        await handleTokenResponse(tokenResponse)
        return true
      } else {
        const errorDetail = (error as { detail?: string })?.detail
        setError(errorDetail || 'SSO authentication failed')
        return false
      }
    } catch (err) {
      console.error('SSO authentication error:', err)
      setError(err instanceof Error ? err.message : 'SSO authentication failed')
      return false
    } finally {
      setIsLoading(false)
    }
  }

  const socialLogin = async (
    provider: string,
    loginData: unknown,
  ): Promise<void> => {
    setIsLoading(true)
    setError(null)

    try {
      const { data, error } = await fetchClient.POST('/auth/social_login', {
        body: { provider, data: loginData } as never,
      })

      if (!error && data) {
        const tokenResponse = data as unknown as TokenResponse
        await handleTokenResponse(tokenResponse)
      } else {
        const errorDetail = (error as { detail?: string })?.detail
        setError(errorDetail || 'Social login failed')
        throw new Error(errorDetail || 'Social login failed')
      }
    } catch (err) {
      console.error('Social login error:', err)
      setError(err instanceof Error ? err.message : 'Social login failed')
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const value: AuthContextType = {
    isAuthenticated,
    user,
    accessToken,
    refreshToken,
    login,
    logout,
    resetPassword,
    ssoAuthenticate,
    socialLogin,
    isLoading,
    error,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
